import React, { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import Checkbox from '@salesforce/design-system-react/components/checkbox';
import Icon from '@salesforce/design-system-react/components/icon';
import IconSettings from '@salesforce/design-system-react/components/icon-settings';
import SetupAssistant from '@salesforce/design-system-react/components/setup-assistant';
import SetupAssistantStep from '@salesforce/design-system-react/components/setup-assistant/step';
import ProgressBar from '@salesforce/design-system-react/components/progress-bar';
import { Statuses } from 'interfaces/ITask.interface';
import { useTranslation } from 'react-i18next';
import CQSpinner from 'components/cq-spinner';
import CQProgressIndicator from 'components/cq-progress-indicator';
import {CQManualTaskModal} from 'components/cq-manual-task-modal';
import DefaultTaskEngine from 'tasks/task-engine';
import { IPastRun, ITaskRun } from '../../interfaces/IHistory.interface';
import { IBaseTask, IUpgradeSchema } from 'interfaces/IUpgradeSchema.interface';
import { CQAppConstant } from 'app-constants';
import { SFConnection } from 'common/sf-connector';
import { getHeadingText, getProcessAndLabelKey, getMigrationDisplayName } from 'common/module.utils';

interface IUpdate {
	module: IUpgradeSchema[],
	selectedTasks?: any;
	setSelectedTasks?: any;
	matchingTasks?: boolean;
	activeTask?: any;
	runSkippedTasks?: boolean;
	setIsSuccess: Dispatch<SetStateAction<boolean>>;
	setSelectedModuleIdentifier: Dispatch<SetStateAction<string>>;
	setSelectedFormodule: Dispatch<SetStateAction<string>>;
	currentRunMap: IPastRun;
  	onProceed: (isManualTaskCompleted?:boolean) => Promise<void>;
}

/**
 * Updates component is responsible for rendering the setup assistant steps for installing modules.
 * It manages the state of selected tasks, progress, and completed steps.
 *
 * @param {IUpdate} props - The properties passed to the component.
 * @returns {JSX.Element} The rendered Updates component.
 */
function Updates(props: IUpdate) {
	const [childSelectedTasks, setChildSelectedTasks] = useState<{[key: string]: boolean }>({});
	const [progress, setProgress] = useState<number>(0);
	const [subStepProgressMap, setSubStepProgressMap] = useState<{ [key: string]: number; }>({});
	const [completedTask, setCompletedTask] = useState<{ id: string; label: string }[]>([]);
	const [disabledTask, setDisabledTask] = useState<{ id: string; label: string }[]>([]);
	const [failedTasks, setFailedTasks] = useState<ITaskRun>();
	const [errorTasks, setErrorTasks] = useState<{ id: string; label: string }[]>([]);
	const [completedModulesSet,setCompletedModulesSet] = useState<Set<string|undefined>>(new Set<string>())
	const [activeManualStep,setActiveManualStep] = useState<IBaseTask | null>(null);
	const [pastSkippedTasks, setPastSkippedTasks] = useState<{[key: string]: boolean }>({});	
	const { t, i18n } = useTranslation();

	/**
	 * Returns the parent task name declared for a child task via `dependencies[].parentTask`.
	 * The match is based on `inputs.taskName` within the same module.
	 */
	const getParentTaskName = (task: IBaseTask): string | undefined =>
		task.dependencies?.find((dep) => dep.parentTask)?.parentTask;

	const getModuleKey = (module: IUpgradeSchema) => `${module.moduleIdentifier}:${module.forModule}`;
	const getTaskKey = (moduleKey: string, taskName?: string) => `${moduleKey}:${taskName || ''}`;

	/**
	 * Builds lookup maps for parent-child task relationships inside the current module list.
	 * - taskNameToTask: taskName -> task
	 * - childrenByParentName: parent taskName -> child tasks
	 */
	const { taskNameToTask, childrenByParentName, taskModuleKeyByUniqueName } = useMemo(() => {
		const taskMap = new Map<string, IBaseTask>();
		const childMap = new Map<string, IBaseTask[]>();
		const taskModuleKeyMap = new Map<string, string>();
		props.module.forEach((module: IUpgradeSchema) => {
			const moduleKey = getModuleKey(module);
			module.steps.forEach((task: IBaseTask) => {
				if (task.uniqueName) {
					taskModuleKeyMap.set(task.uniqueName, moduleKey);
				}
				const taskName = task.inputs.taskName;
				const taskKey = getTaskKey(moduleKey, taskName);
				if (taskName) {
					taskMap.set(taskKey, task);
				}
				const parentTaskName = getParentTaskName(task);
				if (parentTaskName) {
					const parentKey = getTaskKey(moduleKey, parentTaskName);
					const existing = childMap.get(parentKey) || [];
					existing.push(task);
					childMap.set(parentKey, existing);
				}
			});
		});
		return { taskNameToTask: taskMap, childrenByParentName: childMap, taskModuleKeyByUniqueName: taskModuleKeyMap };
	}, [props.module]);

	/**
	 * Enforces parent task selection on child tasks and locks children as disabled.
	 * Returns new selection/disabled structures without mutating inputs.
	 */
	function applyParentSelection(
		selectedMap: { [key: string]: boolean },
		disabledList: { id: string; label: string }[],
	) {
		const nextSelectedMap = { ...selectedMap };
		let nextDisabled = [...disabledList];
		childrenByParentName.forEach((children, parentTaskKey) => {
			const parentTask = taskNameToTask.get(parentTaskKey);
			if (!parentTask) return;
			const parentKey = parentTask.uniqueName || '';
			const parentSelected = !!nextSelectedMap[parentKey];
			children.forEach((child) => {
				const childKey = child.uniqueName || '';
				if (!childKey) return;
				nextSelectedMap[childKey] = parentSelected;
				if (!nextDisabled.find((step) => step.id === childKey)) {
					nextDisabled.push({ id: childKey, label: child.inputs.taskName });
				}
			});
		});
		return { selectedMap: nextSelectedMap, disabledList: nextDisabled };
	}

	useEffect(() => {
        let childSelectedTask:{[key: string]: boolean} = { ...(props.selectedTasks || {}) };
		let defaultDisabledTasks:{ id: string; label: string }[] = [];
        props.module.forEach((module: IUpgradeSchema) => {
            module.steps.forEach((task: IBaseTask) => {
                const taskKey = task.uniqueName || "";
                if (task.defaultChecked === false) {
					defaultDisabledTasks.push({ id: taskKey, label: task.inputs.taskName });
					if (childSelectedTask[taskKey] === undefined) {
						childSelectedTask[taskKey] = false;
					}
					return;
				}
				if (childSelectedTask[taskKey] === undefined) {
					childSelectedTask[taskKey] = true;
				}
            });
        });
		if (props.runSkippedTasks) {
			// Derive pastSkippedTasks from parent's selectedTasks (already filtered by cq-submodules)
			setPastSkippedTasks(props.selectedTasks || {});
			// Use parent's selectedTasks for skipped tasks scenario
			childSelectedTask = { ...(props.selectedTasks || {}) };
		}
		const { selectedMap, disabledList } = applyParentSelection(childSelectedTask, defaultDisabledTasks);
		setDisabledTask(disabledList);
        setChildSelectedTasks(selectedMap);
		props.setSelectedTasks?.(selectedMap);
    }, [props.runSkippedTasks, props.module, childrenByParentName, taskNameToTask])

	// Mirrors parent's selectedTasks locally; must not call props.setSelectedTasks or it loops with the effect above.
	useEffect(() => {
		if (props.selectedTasks) {
			setChildSelectedTasks(props.selectedTasks);
		}
	}, [props.selectedTasks]);

	useEffect(() => {
		getCompletedSteps();
		fetchOverallProgress();
		fetchSubStepProgress();
		const activeManualTask:IBaseTask = props.activeTask?.isManual && props.module.flatMap(module => module.steps).find(step => step.uniqueName === props.activeTask?.uniqueName);
		if(activeManualTask){
			setActiveManualStep(activeManualTask);
		}
	}, [props.activeTask, disabledTask, props.currentRunMap]);

	/**
	 * Fetches the progress of sub-steps for each module and updates the state with the progress map.
	 *
	 * @async
	 * @function fetchSubStepProgress
	 * @returns {Promise<void>} A promise that resolves when the progress map has been updated.
	 */
	async function fetchSubStepProgress() {
		const progressMap: { [key: string]: number } = {};
		for (const module of props.module) {
			progressMap[module.forModule + module.moduleIdentifier] = await fetchModuleTaskProgress(module);
		}
		setSubStepProgressMap(progressMap);
	}

	/**
	 * Handles the change event for a checkbox input.
	 * Parent-controlled tasks are skipped here because their checked state
	 * is derived from the parent task selection.
	 * @param evt - The change event triggered by the checkbox input.
	 * @param task - The task object associated with the checkbox.
	 */
	function handleCheckboxChange (evt:React.ChangeEvent<HTMLInputElement>, task:IBaseTask) {
		const parentTaskName = getParentTaskName(task);
		const moduleKey = task.uniqueName ? taskModuleKeyByUniqueName.get(task.uniqueName) : undefined;
		const parentTask = parentTaskName && moduleKey
			? taskNameToTask.get(getTaskKey(moduleKey, parentTaskName))
			: undefined;
		if (parentTaskName && parentTask) {
			return;
		}
		const isChecked = evt.target.checked
		const updatedSelection = {
			...(props.selectedTasks || {}),
			[task.uniqueName || ""]: isChecked,
		};
		const { selectedMap } = applyParentSelection(updatedSelection, disabledTask);
		props.setSelectedTasks(selectedMap);
		setChildSelectedTasks({
			...childSelectedTasks,
			...selectedMap,
		});
		updateDisabledSteps(isChecked, task, selectedMap);
	}

	/**
	 * Returns the HTML from a translation key, falling back to a raw string from the task JSON.
	 * `{{baseURL}}` is replaced with the instance URL of the org being upgraded. Placeholders are passed to `t`
	 * unescaped, so they are not blanked or encoded by i18next. If the instance URL is not available yet, links
	 * built from `{{baseURL}}` would point at the wrong host, so they are shown as plain text instead.
	 */
	function getTaskHTML (key: string, fallback?: string): string | undefined {
		const missingBaseURL = CQAppConstant.MISSING_BASE_URL;
		const baseURL = SFConnection.getBaseURL() || missingBaseURL;
		const html = i18n.exists(key)
			? t(key, { baseURL, interpolation: { escapeValue: false } })
			: fallback?.split('{{baseURL}}').join(baseURL);
		if (!html) {
			return undefined;
		}
		return html
			.replace(new RegExp(`<a\\b[^>]*href\\s*=\\s*["']${missingBaseURL}[^"']*["'][^>]*>([\\s\\S]*?)</a>`, 'gi'), '$1')
			.split(missingBaseURL).join('');
	}

	/**
	 * Renders a setup assistant action as a toggleable checkbox.
	 *
	 * - Parent-controlled tasks are displayed as disabled and mirror the parent selection.
	 * - Non-parent tasks follow optional/skipped rules and can be toggled by the user.
	 *
	 * @param {ITask} task - The task object containing details for rendering the checkbox.
	 * @returns {JSX.Element} A checkbox component with toggle functionality.
	 */
	function renderSetupAssistantAction(task: IBaseTask) {
		const parentTaskName = getParentTaskName(task);
		const moduleKey = task.uniqueName ? taskModuleKeyByUniqueName.get(task.uniqueName) : undefined;
		const parentTask = parentTaskName && moduleKey
			? taskNameToTask.get(getTaskKey(moduleKey, parentTaskName))
			: undefined;
		const parentSelection = parentTask ? props.selectedTasks?.[parentTask.uniqueName || ""] : undefined;
		const hasParentTask = !!parentTaskName;
		const isParentControlled = hasParentTask && !!parentTask;
		const isChecked = isParentControlled ? !!parentSelection : childSelectedTasks[task.uniqueName || ""];
		const isDisabled =
			hasParentTask ||
			(props.runSkippedTasks ? !pastSkippedTasks[task.uniqueName || ''] : task.inputs.isOptional !== true);
		return (
			<div className='slds-grid slds-gutters'>
				<Checkbox
					className={"slds-m-right_small"}
					disabled={isDisabled}
					labels={{
						toggleDisabled: t("deselected"),
						toggleEnabled: t("selected"),
					}}
					variant="toggle"
					id={task.uniqueName}
					checked={isChecked}
					onChange={(evt) => handleCheckboxChange(evt, task) }
				/>
			</div>
		);
	}

	/**
	 * Generates sub-steps for installation tasks.
	 *
	 * @param {ITask[]} installationTasks - The list of installation tasks.
	 * @returns {any} An array of objects representing sub-steps.
	 *
	 * Each sub-step object contains:
	 * - `id`: The unique name of the task.
	 * - `label`: The localized name of the task if available, otherwise the task name.
	 * - `onRenderSetupAssistantAction`: A JSX element representing a toggleable checkbox for the task.
	 *
	 * The checkbox is disabled if the task is not optional. The checkbox state is managed by `childSelectedTasks` and `props.selectedTasks`.
	 */
	function generateSubStepsForTasks(steps: IBaseTask[]): any {
        return steps.map((task: IBaseTask, index: number) => {
			let taskName = task.inputs.taskName;
			const title = (task.inputs.label ? task.inputs.label : i18n.exists('task_' + taskName) ? t('task_' + taskName) : taskName);
            return {
                id: task.uniqueName,
                label: title,
                description: getTaskHTML('task_' + taskName + '_description', task.inputs.description),
                onRenderSetupAssistantAction: renderSetupAssistantAction(task),
            }
        })
    }

	 /**
     * Updates disabled steps based on the checkbox state and parent-task rules.
     * Re-applies parent-child enforcement using the latest selected map.
     *
     * @param {boolean} isChecked - The state of the checkbox.
     * @param {ITask} task - The task object.
     * @param selectedMap - Latest selected state to enforce parent-child rules.
     */
	function updateDisabledSteps (
		isChecked:Boolean,
		task:IBaseTask,
		selectedMap: { [key: string]: boolean },
	) {
		setDisabledTask((prev) => {
			let next = [...prev];
			if (!isChecked) {
				if (!next.find((step) => step.id === task.uniqueName)) {
					next.push({ id: task.uniqueName || '', label: task.inputs.taskName });
				}
			} else {
				next = next.filter((step) => step.id !== task.uniqueName);
			}
			const updated = applyParentSelection(selectedMap, next);
			return updated.disabledList;
		});
	}

	/**
	 * Retrieves and processes the completed steps from the last run.
	 * 
	 * Updates the following state values:
	 * - `failedTasks`: Sets the first failed task found in the last run
	 * - `errorTasks`: Maps all failed tasks to an array of objects with id and label properties
	 * - `completedTask`: Maps completed tasks (including past completed tasks) to an array of objects with id and label properties
	 * 
	 * @async
	 * @function getCompletedSteps
	 * @returns {Promise<void>}
	 */
    async function getCompletedSteps() {

		if (props.currentRunMap && props.currentRunMap.tasks.length > 0) {
			//sets the total task length that needs to be executed
			const completedTasks = props.currentRunMap.tasks.filter((task) => task.status === Statuses.Completed && task.isSkipped === false);
			const failedTasks = props.currentRunMap.tasks.find((task) => task.status === Statuses.Failed);
			if(failedTasks) {
				setFailedTasks(failedTasks);
				setActiveManualStep(null);
			} else {
				setFailedTasks(undefined);
			}
			setErrorTasks(props.currentRunMap.tasks.filter((task) => task.status === Statuses.Failed).map((task) => ({ id: task.uniqueName || "", label: task.name})));
			setCompletedTask(completedTasks.map((task) => ({ id: task.uniqueName || "", label: task.name })));
		}
    };


	/**
	 * Fetches and calculates the overall progress of tasks.
	 * Computes the completion percentage based on completed tasks minus skipped tasks,
	 * divided by total remaining tasks minus disabled tasks.
	 * Updates the progress state with the calculated percentage.
	 */
	async function fetchOverallProgress() {
		let totalCompleted = 0;
		let totalTasksLength = 0;
		let totalTasks = props.currentRunMap.tasks.filter(t => t.isSkipped === false);
		totalTasksLength = totalTasks.length;
		totalCompleted = totalTasks.filter((task) => task.status === Statuses.Completed).length;
		setProgress(totalCompleted === 0 ? 0 : Math.round(((totalCompleted) / (totalTasksLength) * 100)));
	}
	
	
	/**
	 * Calculates the progress percentage of a module's tasks and updates the completed modules set.
	 * @param module - The upgrade schema module containing the steps/tasks to track
	 * @returns The progress percentage (0-100) of completed tasks relative to enabled tasks, or 0 if no tasks exist
	 * @remarks
	 * - Filters out disabled tasks from the total task count
	 * - Counts completed tasks from the last run and previously completed tasks
	 * - Automatically marks a module as completed when all tasks are done or disabled
	 * - Updates parent component state (selectedForModule, selectedModuleIdentifier, isSuccess) when module completes
	 */
	async function fetchModuleTaskProgress(module: IUpgradeSchema) {
		let tasksToRun = props.currentRunMap.tasks.filter((task) => task.isSkipped === false && module.steps.find((moduleTask:IBaseTask)=>moduleTask.uniqueName === task.uniqueName));
		let completedTasksLength = tasksToRun.filter((task) => task.status === Statuses.Completed).length;
		let totalTasks = tasksToRun.length;

		if (tasksToRun.length !== 0 && completedTasksLength === totalTasks && !completedModulesSet.has(module.forModule)) {
			//If all the tasks of a module have been completed add it to completedModulesSet
			let completedModules = new Set(completedModulesSet);
			completedModules.add(module.forModule);
			setCompletedModulesSet(completedModules);

			props.setSelectedFormodule(module?.name === CQAppConstant.SHARED_MIGRATION.SCRIPT_NAME ? getMigrationDisplayName(module) : (module?.forModule || ''));
			props.setSelectedModuleIdentifier(module?.moduleIdentifier || '');
			props.setIsSuccess(true);
		}
		return totalTasks === 0 ? 0 : Math.round((completedTasksLength / totalTasks) * 100);
	}

	/**
	 * Renders a progress bar with an optional spinner.
	 *
	 * @returns {JSX.Element} The JSX element representing the progress bar.
	 *
	 * @remarks
	 * This component displays a progress bar with a spinner if there is an active task.
	 * The progress bar shows the progress of the active task.
	 * @component
	 * @name progressBar
	 */
	
	function progressBar() {
		let selectedTasksLength = Object.values(props.selectedTasks || {} ).filter(value => value ===true ).length;
		let matchingTasks = props.matchingTasks ? completedTask.length : 0;
		let isActiveTab = props.activeTask && props.module.some((module:IUpgradeSchema) =>
			module.steps.some(
				(task:IBaseTask) => task.uniqueName === props.activeTask?.uniqueName
			)
		);
		const { labelKey, process } = getProcessAndLabelKey(DefaultTaskEngine.currentModule);
		return (<div className="slds-grid slds-gutters slds-align_absolute-center">
			{isActiveTab && (
				<div className="slds-col slds-size_1-of-12 slds-align_absolute-center">
					{props.activeTask.isManual ? (
						<Icon
							assistiveText={{ label: 'Pause' }}
							category="utility"
							name="pause"
							size="medium"
						/>
					) : (
						<CQSpinner />
					)}
				</div>
			)}
			<div className='slds-col slds-size_11-of-12'>
				{isActiveTab &&
					(<ProgressBar
						color="success"
						id="card-setup-assistant-progress-bar"
						labels={{ label: t(labelKey, { progress: matchingTasks, length: selectedTasksLength, process }) }}
						radius="circular"
						value={props.matchingTasks ? progress : 0}
					/>)}
			</div>
		</div>);
	}

	/**
	 * Renders the content for the given module.
	 *
	 * @param {IUpgradeSchema} module - The module containing installation tasks.
	 * @returns {JSX.Element} The rendered CQProgressIndicator component.
	 */
	function renderContent (module:IUpgradeSchema) {
		return (
			<CQProgressIndicator
				completedSteps={completedTask}
				errorSteps={errorTasks}
				id="card-step-3-progress-indicator"
				steps={generateSubStepsForTasks(module.steps)}
				selectedStep={{
					id: props.activeTask ? props.activeTask.uniqueName : "",
					label: props.activeTask?.name ? props.activeTask.name : ""
				}}
			/>
		)
	}

	/**
	 * Renders a warning icon using Salesforce Lightning Design System (SLDS) classes.
	 *
	 * @returns {JSX.Element} A span element containing an SVG icon with warning styling.
	 */
	function renderFigure () {
		return (
			<span className="slds-icon_container slds-icon-utility-warning slds-icon-text-error slds-p-around_medium ">
				<svg className="slds-icon slds-icon_small " aria-hidden="true">
					<use xlinkHref="/assets/icons/utility-sprite/svg/symbols.svg#warning"></use>
				</svg>
			</span>
		) 
	}

	/**
	 * Generates an array of `SetupAssistantStep` components based on the provided module data.
	 *
	 * @param {Object} props - The properties passed to the component.
	 * @param {Array} props.module - An array of module objects containing information about each module.
	 * @param {Object} props.activeTask - The currently active task object.
	 * @param {string} props.activeTask.uniqueName - The unique name of the active task.
	 * @param {string} props.activeTask.name - The name of the active task.
	 * @returns {JSX.Element[]} An array of `SetupAssistantStep` components.
	 */
	const getAssistantSteps = props.module.map((module: IUpgradeSchema) => {
		const hasError = !!failedTasks && props.currentRunMap.moduleId === DefaultTaskEngine.currentModule && failedTasks.forModule === module.forModule;
		const headingText = getHeadingText(DefaultTaskEngine.currentModule, module);
		
		//In JSX we cannot provide props conditionally . So we need to provide all the props and then conditionally render the component
		if(hasError){
			return(
				<SetupAssistantStep
					stepNumber={props.module.indexOf(module) + 1}
					className="slds-text-align_left slds-m-vertical_x-large slds-p-vertical_large"
					heading={<span className="slds-text-color_error">{headingText}</span>}
					id={module.forModule}
					isExpandable
					key={module.forModule}
					onRenderContent={() => renderContent(module)}
					onRenderFigure={ renderFigure }
					progress={subStepProgressMap[module.forModule + module.moduleIdentifier] || 0}
				/>
			)
		}
		return (
			<SetupAssistantStep
				stepNumber={props.module.indexOf(module) + 1}
				className="slds-text-align_left slds-m-vertical_x-large slds-p-vertical_large"
				heading={headingText}
				id={module.forModule}
				isExpandable
				key={module.forModule}
				onRenderContent={() => renderContent(module)}
				progress={subStepProgressMap[module.forModule + module.moduleIdentifier] || 0}
			/>
		)
	}
	)
	return (
		<>
			<IconSettings iconPath="/assets/icons">
				<SetupAssistant
					id="card-setup-assistant"
					isCard
					progressBar={progressBar()}
				>
					{getAssistantSteps}
				</SetupAssistant>
			</IconSettings>
			{props.activeTask && activeManualStep && !errorTasks.length && <CQManualTaskModal activeTask={activeManualStep} onProceed={props.onProceed}/>}
		</>
	);
}
export default Updates;